'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import copy from 'copy-to-clipboard';
import { CheckIcon, CopyIcon } from 'lucide-react';

import { cn } from '~/lib/utils';

type CopyContextType = {
  copy: (event: React.MouseEvent<HTMLButtonElement>) => void;
  textToCopy: string;
  isCopied: boolean;
};

const CopyContext = createContext<CopyContextType | undefined>(undefined);

const useCopy = () => {
  const context = useContext(CopyContext);
  if (!context) {
    throw new Error('useCopy must be used within a CopyProvider');
  }
  return context;
};

function CopyToClipboard({
  textToCopy,
  children,
  className,
  ...props
}: Omit<React.ComponentPropsWithoutRef<'button'>, 'onClick'> & {
  textToCopy: string;
}) {
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (isCopied) {
      const timeoutId = setTimeout(() => {
        setIsCopied(false);
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [isCopied]);

  const value = useMemo(
    () => ({
      isCopied,
      copy: async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        setIsCopied(await copy(textToCopy));
      },
      textToCopy,
    }),
    [textToCopy, isCopied],
  );

  return (
    <CopyContext.Provider value={value}>
      <CopyToClipboardButton className={cn('cursor-pointer', className)} {...props}>
        {children}
      </CopyToClipboardButton>
    </CopyContext.Provider>
  );
}

function CopyToClipboardButton({
  className,
  children,
  ...props
}: Omit<React.ComponentPropsWithoutRef<'button'>, 'onClick'>) {
  const { isCopied, copy, textToCopy } = useCopy();

  return (
    <button className={className} onClick={copy} data-text-to-copy={textToCopy} data-copied={isCopied} {...props}>
      {children}
    </button>
  );
}

function CopyToClipboardIcon({ className }: { className?: string }) {
  const { isCopied } = useCopy();

  const Icon = isCopied ? CheckIcon : CopyIcon;

  return <Icon className={className} />;
}

function CopyToClipboardText({ defaultText, copiedText }: { defaultText?: string; copiedText?: string }) {
  const { isCopied } = useCopy();

  return isCopied ? copiedText : defaultText;
}

export { CopyToClipboard, CopyToClipboardText, CopyToClipboardIcon };
